import { Plus, Minus, Ban } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import { useCart } from "../context/CartContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export default function FoodCard({ item, index = 0, allergensVocab = [] }) {
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const variants = item.variants && item.variants.length > 0 ? item.variants : null;
  const [variantIdx, setVariantIdx] = useState(0);
  const selectedVariant = variants ? variants[variantIdx] : null;
  const displayPrice = selectedVariant ? selectedVariant.price : item.price;
  const displayUnit = selectedVariant ? selectedVariant.unit : item.unit;
  const displayNote = selectedVariant?.note || "";
  const soldOut = !!item.sold_out;

  const tags = useMemo(() =>
    (item.allergens || []).map((k) => allergensVocab.find((t) => t.key === k)).filter(Boolean),
    [item.allergens, allergensVocab]
  );

  const handleAdd = () => {
    if (soldOut) return;
    add(item, qty, selectedVariant);
    toast.success(`${qty} × ${item.name}${selectedVariant ? ` — ${selectedVariant.name}` : ""} added`, {
      description: "Open your cart to check out",
    });
    setQty(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, ease: [0.2, 0.9, 0.3, 1], delay: (index % 6) * 0.05 }}
      className={`chop-card chop-shadow overflow-hidden group flex flex-col ${soldOut ? "opacity-60" : ""}`}
      data-testid={`food-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F4EFEB]">
        <img
          src={item.image}
          alt={item.name}
          className={`w-full h-full object-cover transition-transform duration-700 ${soldOut ? "grayscale" : "group-hover:scale-[1.06]"}`}
          loading="lazy"
        />
        <span className="absolute top-3 left-3 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-[10px] font-label text-[#0E2B13]">
          {item.category}
        </span>
        {displayUnit && !soldOut && (
          <span data-testid={`food-unit-${item.id}`} className="absolute top-3 right-3 rounded-full bg-[#1A6B2E] text-white px-3 py-1 text-[10px] font-label">
            {displayUnit}
          </span>
        )}
        {soldOut && (
          <span data-testid={`sold-out-badge-${item.id}`} className="absolute top-3 right-3 rounded-full bg-[#D02030] text-white px-3 py-1 text-[10px] font-label inline-flex items-center gap-1">
            <Ban className="w-3 h-3" /> Sold out
          </span>
        )}
      </div>

      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl md:text-2xl">{item.name}</h3>
          <span data-testid={`food-price-${item.id}`} className="font-display text-lg text-[#D02030] shrink-0">
            ${displayPrice.toFixed(2)}
          </span>
        </div>
        <p className="text-sm text-[#4E5C4F] mt-2 line-clamp-2 flex-1">{item.description}</p>

        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`allergens-${item.id}`}>
            {tags.map((t) => (
              <span
                key={t.key}
                data-testid={`allergen-tag-${item.id}-${t.key}`}
                className={`rounded-full px-2 py-0.5 text-[10px] font-label border ${
                  t.kind === "dietary"
                    ? "bg-[#1A6B2E]/10 text-[#1A6B2E] border-[#1A6B2E]/25"
                    : "bg-[#D02030]/10 text-[#D02030] border-[#D02030]/25"
                }`}
              >
                {t.kind === "warning" ? "Contains " : ""}{t.label}
              </span>
            ))}
          </div>
        )}

        {variants && (
          <div className="mt-4" data-testid={`variants-${item.id}`}>
            <p className="text-[10px] font-label text-[#4E5C4F] mb-2">Choose size</p>
            <Select
              value={String(variantIdx)}
              onValueChange={(v) => setVariantIdx(Number(v))}
              disabled={soldOut}
            >
              <SelectTrigger
                data-testid={`variant-trigger-${item.id}`}
                className="rounded-full border-[#1A6B2E]/25 bg-white text-[#0E2B13] font-medium h-10"
              >
                <SelectValue placeholder="Select a size" />
              </SelectTrigger>
              <SelectContent>
                {variants.map((v, i) => (
                  <SelectItem
                    key={v.name}
                    value={String(i)}
                    data-testid={`variant-${item.id}-${i}`}
                  >
                    {v.name} — ${v.price.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {displayNote && (
              <p data-testid={`variant-note-${item.id}`} className="mt-2 text-[11px] italic text-[#4E5C4F]">
                {displayNote}
              </p>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full border border-[#1A6B2E]/20 p-1">
            <button
              data-testid={`qty-minus-${item.id}`}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-8 h-8 grid place-items-center rounded-full hover:bg-[#F4EFEB] transition-colors"
              aria-label="Decrease"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span data-testid={`qty-value-${item.id}`} className="w-6 text-center text-sm font-semibold tabular-nums">
              {qty}
            </span>
            <button
              data-testid={`qty-plus-${item.id}`}
              onClick={() => setQty((q) => Math.min(50, q + 1))}
              className="w-8 h-8 grid place-items-center rounded-full hover:bg-[#F4EFEB] transition-colors"
              aria-label="Increase"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            data-testid={`add-to-cart-${item.id}`}
            onClick={handleAdd}
            disabled={soldOut}
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-[#1A6B2E]"
          >
            {soldOut ? "Sold out" : "Add to cart"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
